import { FormSectionHandler } from '../entity.handler';
import { AssetResponse, AssetUpdateRequest, IdName, AssetParameters } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { AssetGroupingService } from 'app/services';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { IdNameComboValueHandlers, IdNameSelectSearchValueHandlers, TextField, ComboField, CheckListField, IdNameArrayComboValueHandlers, EntityUtils, TagsField, CostCentreField, postImageForm, dataURItoFile } from '../utils';
import { ICONS, ASSET_ICONS } from '../icons';
import { HttpClient } from '@angular/common/http';
import { urlToDataUrl } from 'app/shared/utils/dataUrl';
import { KeyImageEditorModalComponent } from 'app/shared/components/image-editor';

interface EditorValues {
    name?: string;
    assetType?: IdName;
    color?: string;
    tags?: string[];
    costCentre?: IdName;
    groups?: IdName[];
    assetTag?: IdName;
    parameters?: AssetParameters;
}

export class AssetDetailsSectionHandler implements FormSectionHandler<AssetResponse, EditorValues> {

    type = 'form';
    icon = ICONS.asset;
    assetTypeIcon = ICONS.asset;
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private grouping: AssetGroupingService,
        private httpClient: HttpClient
    ) {
    }

    private colors = ['blue', 'lightblue', 'green', 'olive', 'orange', 'pink', 'purple', 'red', 'teal', 'yellow', 'white', 'gray'];
    private properties = ['name', 'assetType', 'color', 'costCentre', 'groups', 'tags', 'assetTag'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.DETAILS.${key}`, values);
    }

    applyUpdate(entity: AssetResponse, payload: AssetUpdateRequest): Promise<AssetResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, payload));
    }

    mapEntityToEditor(entity: AssetResponse): Promise<EditorValues> {
        return Promise.resolve(EntityUtils.mergeFields<EditorValues>(this.properties, entity, {}));
    }

    mapEditorToEntity(entity: AssetUpdateRequest, values: EditorValues): Promise<AssetUpdateRequest> {

        if (values.tags) {
            values.tags = values.tags.map(x => x.trim()).filter(x => x);
        }

        return Promise.resolve(EntityUtils.mergeFields(this.properties, values, entity));
    }

    async getFields(entity: AssetResponse): Promise<FormBuilderField[]> {

        return Promise.all([
            this.grouping.getAssetTypes(this.ownerId),
            this.grouping.getAssetGroups(this.ownerId),
        ]).then(async ([assetTypes, assetGroups]) => {

            if (entity && entity.assetType) { // work out the correct icon for the entity
                const at = assetTypes.find(x => x.id === entity.assetType.id);
                this.assetTypeIcon = ASSET_ICONS[at?.data?.tag] || ICONS.asset;
                this.icon = this.assetTypeIcon || this.icon;

                try {
                    this.icon = await urlToDataUrl(this.httpClient, `${this.admin.apiEndpoint}/entities/assets/${entity.id}/avatar`, this.admin.apiAccessToken);
                } catch {
                }
            }

            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                TagsField('tags', this.translate('FIELDS.TAGS'), false),
                ComboField('assetType', this.translate('FIELDS.TYPE'), true, EntityUtils.treeToComboValues(assetTypes), IdNameComboValueHandlers),
                ComboField('color', this.translate('FIELDS.COLOR'), true, this.colors.map(x => ({ key: x, value: this.translate(`COLORS.${x.toUpperCase()}`) })).sort((a, b) => a.value.localeCompare(b.value))),
                CostCentreField(this.ownerId, 'costCentre', this.translate('FIELDS.COST_CENTRE'), true),
                CheckListField('groups', this.translate('FIELDS.GROUPS'), true, EntityUtils.treeToComboValues(assetGroups), IdNameArrayComboValueHandlers),
                { type: 'assettagfilter', id: 'assetTag', title: this.translate('FIELDS.ASSET_TAG'), max: 1, required: false, ...IdNameSelectSearchValueHandlers, options: { ownerId: this.ownerId } },
            ].filter(x => x);
        }).then(fields => EntityUtils.prepareBulk(!entity, ['assetType', 'color', 'costCentre', 'groups'], fields));
    }

    async onIconClicked(entity: AssetResponse): Promise<AssetResponse> {
        const result = await KeyImageEditorModalComponent.open(this.admin.modal, this.admin.translate('EDITORS.ASSET.DETAILS.FIELDS.PHOTO'), this.icon,
            { paddingPercentage: 0 },
            {
                visible: true,
                width: 250,
                height: 250,
            },
            100);
        if (result) {
            this.icon = result.preview;
            await postImageForm(this.httpClient, `${this.admin.apiEndpoint}/entities/assets/${entity.id}/avatar?size=large`, this.admin.apiAccessToken, dataURItoFile(result.preview));
        }
        if (result === null) {
            this.icon = this.assetTypeIcon;
            await this.admin.applyUpdate(api => api.entities.deleteAssetAvatar(entity.id));
        }
        return entity;
    }
}
